import { Injectable } from '@angular/core';

import { AzavistaApiService, EventAccess, EventPublish, IDependency, IEvent, IEventActivity, IProduct, ProductGroupEntityType, convertDependencyToV2 } from '@azavista/servicelib';
import { IField, IIdWithLabel, IObjectWithId, IRowAction } from '@azavista/components/shared';
import { DependencyRowActionId } from './enums';
import { ICalculatedDependencyRow } from './interfaces';
import { formatFieldsToFilterFieldsArray } from '../event-content-website.service';
import { SharedService } from 'src/app/shared/shared.service';
import { RequiredScopesService } from 'src/app/shared/required-scopes.service';
import { SettingParentType, TranslationKey } from 'src/app/shared/enums';
import { IconName } from 'src/app/shared/icons-enum';
import { TRANSLATION_MAP } from 'src/app/shared/const';

export enum DependencyPaymentMethod {
    online = 'online',
    invoice = 'invoice',
    [''] = '',
}

@Injectable({
    providedIn: 'root'
})
export class DependenciesService {
    constructor(private apiSvc: AzavistaApiService, private sharedSvc: SharedService, private rsSvc: RequiredScopesService) {
    }

    addEventStartAndEndDatesFields(fields: IField[]): void {
        const eventStartDateField = {
            id: 'Event.start', name: 'Event.start', label: this.sharedSvc.translate(TranslationKey.eventStart),
            schema: { type: 'string', format: 'date-time' }
        } as IField;
        const eventEndDateField = {
            id: 'Event.end', name: 'Event.end', label: this.sharedSvc.translate(TranslationKey.eventEnd),
            schema: { type: 'string', format: 'date-time' }
        } as IField;
        fields.push(eventStartDateField);
        fields.push(eventEndDateField);
    }

    getDependencyActions(): IRowAction[] {
        const result: IRowAction[] = [{
            label: this.sharedSvc.translate(TranslationKey.viewOrEdit),
            id: DependencyRowActionId.viewOrEdit, icon: IconName.edit, singleSelectionOnly: true,
            requiredScopes: this.rsSvc.getRequiredUpdateEventWebsiteDependenciesScopes()
        }, {
            label: this.sharedSvc.translate(TranslationKey.delete),
            id: DependencyRowActionId.delete, icon: IconName.style, singleSelectionOnly: true,
            requiredScopes: this.rsSvc.getRequiredDeleteEventWebsiteDependenciesScopes()
        }];
        return result;
    }

    getDependenciesDisplayedColumns(): string[] {
        return this.getDependenciesColumns().map(x => x.name);
    }

    getDependenciesColumns(): IField[] {
        const result: IField[] = [];
        result.push(this.getDependencyFieldNameField());
        result.push({
            id: 'criteriaCount', name: 'criteriaCount', label: this.sharedSvc.translate(TranslationKey.criteria),
            category: '',
            schema: { type: 'integer' }, editable: false, builtin: true, visible_for_planners: true
        });
        return result;
    }

    getDependencyFieldNameField(): IField {
        return {
            id: 'name', name: 'name', label: this.sharedSvc.translate(TranslationKey.name), category: '',
            schema: { type: 'string' }, editable: false, builtin: true, visible_for_planners: true
        };
    }

    convertToCalculatedDependencyRows(dependencies: IDependency[]): ICalculatedDependencyRow[] {
        const calculatedRows: ICalculatedDependencyRow[] = [];
        for (const dependency of dependencies) {
            const dependencyV2 = convertDependencyToV2(dependency);
            calculatedRows.push({
                id: dependencyV2.id, name: dependencyV2.name,
                criteriaCount: dependencyV2.criteria
                    ? dependencyV2.criteria.reduce((result, { length }) => result + length, 0)
                    : 0
            });
        }
        return calculatedRows;
    }

    async getDependencyEventActivityIds(eventId: string) {
        return await getDependencyEventActivityIds(eventId, {
            apiSvc: this.apiSvc,
            sharedSvc: this.sharedSvc
        });
    }

    async getDependencyEventProductFields(eventId: string) {
        return await getDependencyEventProductFields(eventId, {
            apiSvc: this.apiSvc,
            sharedSvc: this.sharedSvc
        });
    }

    getDependencyWorkflow() {
        return getDependencyWorkflow({ sharedSvc: this.sharedSvc });
    }

    getEventSettingsFields(): IField[] {
        return [
            this.sharedSvc.createSelectField({
                label: this.sharedSvc.translate(TRANSLATION_MAP.STATUS),
                name: 'publish',
                id: 'publish',
            }, {
                [EventPublish.PUBLISHED]: TRANSLATION_MAP.PUBLISHED,
                [EventPublish.OFF_LINE]: TRANSLATION_MAP.OFF_LINE,
                [EventPublish.NO_REGISTRATION]: TRANSLATION_MAP.NO_REGISTRATION,
                [EventPublish.APPROVED]: TRANSLATION_MAP.APPROVED,
                [EventPublish.CANCELLED]: TRANSLATION_MAP.CANCELLED,
                [EventPublish.DRAFT]: TRANSLATION_MAP.DRAFT,
            }),
            this.sharedSvc.createSelectField({
                label: this.sharedSvc.translate(TRANSLATION_MAP.VISIBILITY),
                name: 'access',
                id: 'access',
            }, {
                [EventAccess.invited]: TRANSLATION_MAP.ONLY_THOSE_WHO_GET_INVITATIONS,
                [EventAccess.login]: TRANSLATION_MAP.ONLY_THOSE_WITH_A_LOGIN,
                [EventAccess.public]: TRANSLATION_MAP.OPEN_TO_THE_PUBLIC,
            }),
            {
                id: 'offline_date', name: 'offline_date', label: this.sharedSvc.translate(TRANSLATION_MAP.OFFLINE_DATE),
                schema: { type: 'string', format: 'date-time' }
            } as IField,
            {
                id: 'last_registration_date', name: 'last_registration_date', label: this.sharedSvc.translate(TRANSLATION_MAP.REGISTRATION_DEADLINE),
                schema: { type: 'string', format: 'date-time' }
            } as IField,
            {
                id: 'publication_date', name: 'publication_date', label: this.sharedSvc.translate(TRANSLATION_MAP.PUBLICATION_DATE),
                schema: { type: 'string', format: 'date-time' }
            } as IField,
        ];
    }

    async getDependencyFields(event: IEvent) {
        const eventId = event.id;
        const [participantFields, eventFields, activityIdsField, productFields] = await Promise.all([
            this.getParticipantFields(eventId),
            this.apiSvc.getAllEventFields(eventId),
            this.getDependencyEventActivityIds(eventId),
            this.getDependencyEventProductFields(eventId)
        ]);
        const eventSettingsFields = this.getEventSettingsFields();
        const workflowFields = this.getDependencyWorkflow();
        const allDependencyFields = this.sharedSvc.filterOutRelationFields(
            formatFieldsToFilterFieldsArray({
                participantFields, eventFields, activityIdsField, productFields, workflowFields, eventSettingsFields
            }, this.sharedSvc)
        );
        this.sharedSvc.setDateTimeFieldsTimeZone(allDependencyFields, event.timezone);
        this.sharedSvc.sortAlphabetically(allDependencyFields, (item) => item.label);
        return allDependencyFields;
    }

    private async getParticipantFields(eventId: string) {
        const [
            stages,
            participantFields,
            { profile_pages },
            eventSettings
        ] = await Promise.all([
            this.apiSvc.getAllStages(SettingParentType.event, eventId),
            this.apiSvc.getAllEventParticipantFields(eventId),
            this.apiSvc.getAllProfilePages(),
            this.apiSvc.getEventSettings(eventId)
        ]);
        this.sharedSvc.addParticipantStageIdField(participantFields, stages);
        this.sharedSvc.addParticipantProfilePageIdField(participantFields, profile_pages);
        const fieldsOptions: any[] = [];
        this.sharedSvc.addFieldOptionsForStageField(stages, fieldsOptions);
        await this.sharedSvc.addFieldOptionsForProfilePageField(fieldsOptions, participantFields, profile_pages);
        const eventLanguages = eventSettings?.settings?.languages ?? [];
        await this.sharedSvc.modifyPreferredLanguageField(participantFields, eventLanguages);
        return participantFields;
    }
}

export async function getDependencyEventActivityIds(eventId: string, deps: { sharedSvc: SharedService, apiSvc: AzavistaApiService }): Promise<IField> {
    type EventActivityWithId = IEventActivity & IObjectWithId;
    const { sharedSvc, apiSvc } = deps;
    const allActivities: EventActivityWithId[] = await apiSvc.getAllEventActivities(eventId) as any;
    const allActivitiesEnum = allActivities.reduce((record, { name, id }) => ({
        ...record,
        [id]: name
    }), {});
    return sharedSvc.createSelectField({
        id: 'ParticipantActivity_ids',
        name: 'ParticipantActivity_ids',
        label: sharedSvc.translate(TRANSLATION_MAP.ACTIVITIES),
    }, allActivitiesEnum as any);
}

export async function getDependencyEventProductFields(eventId: string, deps: { sharedSvc: SharedService, apiSvc: AzavistaApiService }): Promise<IField[]> {
    const { sharedSvc } = deps;
    const productPrefix = '';
    return [
        ...(await getDependencyEventProductsFields(
            eventId,
            [
                { id: 'Product_ids', label: productPrefix + sharedSvc.translate(TRANSLATION_MAP.PRODUCTS) },
                { id: 'Product_limit_reached', label: productPrefix + sharedSvc.translate(TRANSLATION_MAP.MAXIMUM_PRODUCTS_PER_BUYER_LIMIT_REACHED) }
            ],
            deps
        )),
        {
            id: 'Product_ticket_count',
            name: 'Product_ticket_count',
            builtin: false,
            category: '',
            editable: false,
            schema: { type: 'number' },
            type: 'number',
            label: productPrefix + sharedSvc.translate(TRANSLATION_MAP.PRODUCT_TICKET_COUNT),
        },
        {
            id: 'Product_total_price',
            name: 'Product_total_price',
            builtin: false,
            category: '',
            editable: false,
            schema: { type: 'number' },
            type: 'number',
            label: productPrefix + sharedSvc.translate(TRANSLATION_MAP.PRODUCT_TOTAL_PRICE),
        },
    ];
}

export async function getDependencyEventProductsFields(eventId: string, fieldIdsWithLabels: IIdWithLabel[], deps: { sharedSvc: SharedService, apiSvc: AzavistaApiService }): Promise<IField[]> {
    const { sharedSvc, apiSvc } = deps;
    const allProducts: IProduct[] = await apiSvc.getAllPagedItems(
        req => apiSvc.getProductsForEntity(ProductGroupEntityType.event, eventId, req),
        res => res.products
    );
    const allProductsEnums = allProducts.reduce((record, { name, id }) => ({
        ...record,
        [id]: name
    }), {});
    const allFields = fieldIdsWithLabels.map(fieldIdWithLabel => sharedSvc.createSelectField(
        {
            id: fieldIdWithLabel.id,
            name: fieldIdWithLabel.id,
            label: fieldIdWithLabel.label,
        },
        allProductsEnums as any)
    );
    return allFields;
}

export function getDependencyWorkflow(deps: { sharedSvc: SharedService }): IField[] {
    const { sharedSvc } = deps;
    const category = sharedSvc.translate(TRANSLATION_MAP.WORKFLOW);
    const waitingListField: IField = {
        id: 'Workflow_waitlist',
        schema: { type: 'boolean', default: false },
        type: 'boolean',
        label: `${sharedSvc.translate(TRANSLATION_MAP.WAITINGLIST)}`,
        category,
        name: 'Workflow_waitlist',
        editable: true,
        builtin: false
    };
    const paymentField: IField = {
        ...waitingListField,
        id: 'Workflow_payment',
        name: 'Workflow_payment',
        category,
        label: `${sharedSvc.translate(TRANSLATION_MAP.PAYMENT)}`,
    };
    const paymentMethod = sharedSvc.createSelectField({
        id: 'Workflow_payment_method',
        name: 'Workflow_payment_method',
        category,
        label: `${sharedSvc.translate(TRANSLATION_MAP.PAYMENT_METHOD)}`,
    }, DependencyPaymentMethod as any);
    return [
        waitingListField,
        paymentField,
        paymentMethod,
    ];
}
